import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleNotch,
  CheckCircle,
  Prohibit,
  ChatCircleText,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { questionKeys } from '~/lib/query-keys';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import { Label } from '~/components/ui/label';
import { Badge } from '~/components/ui/badge';
import { chatKeys } from '~/lib/query-keys';
import { useDismissChatWaiting } from '~/hooks/mutations/use-dismiss-chat-waiting';
import {
  draftKey,
  EMPTY_DRAFT,
  useQuestionDrafts,
} from '~/lib/stores/question-draft-store';

type QuestionsStatus = 'pending' | 'answered' | 'cancelled';
type CardStatus = QuestionsStatus | 'superseded' | 'loading';

export interface QuestionOption {
  label: string;
  description?: string;
}

export interface QuestionDef {
  question: string;
  header: string;
  multiSelect: boolean;
  options: QuestionOption[];
}

export interface QuestionAnswer {
  selected: string[];
  other?: string;
}

interface QuestionsBatch {
  id: number;
  questions: QuestionDef[];
  answers?: QuestionAnswer[];
  status: QuestionsStatus;
}

interface QuestionsPayload {
  status?: string;
  questions?: QuestionsBatch;
}

interface QuestionsCardProps {
  output: QuestionsPayload;
  sendMessage?: (text: string) => void | Promise<void>;
  /**
   * Session the surrounding chat surface is bound to. The chat surfaces (zen
   * modal / launcher, FRONT-518/522) run under a per-surface session id
   * (`web:`/`inbox-`), NOT the global one, and the batch is written under THAT
   * session — so the card MUST look up its live status AND submit answers
   * against the same id. Required (no global fallback) so a surface that
   * forgets to thread it is a compile error, not a silently-broken card —
   * which is exactly the regression this fixes. Matches PlanCard /
   * SaveProposalCard, which also take sessionId as a required prop. (FRONT-540)
   */
  sessionId: string;
  /**
   * True while this turn is still streaming (FRONT-1685). The card keeps
   * accepting input during the turn — Submit stays disarmed until it
   * settles, then posts. Sidesteps the FRONT-196 collision class (approving
   * something before the process that produced it has finished) rather than
   * making draft survival depend on it.
   */
  turnActive?: boolean;
}

export const OTHER = '__other__';

/**
 * FRONT-2603: whether a card should draw the eye when it first settles. Only a
 * still-open question met on arrival (a reopened chat), never one that appears
 * while its own turn is streaming in front of the user.
 */
export function spotlightOnArrival(status: CardStatus, dbMatches: boolean, turnActive: boolean): boolean {
  return status === 'pending' && dbMatches && !turnActive;
}

const SPOTLIGHT_MS = 2400;

/**
 * FRONT-2603: the scroll offset that brings a card fully into its scroll
 * container's view, or null when it already is. Moving only this container
 * keeps a page behind the chat from shifting, which `scrollIntoView` would do.
 */
export function revealScrollTop(
  container: { top: number; bottom: number; scrollTop: number },
  card: { top: number; bottom: number },
): number | null {
  if (card.top >= container.top && card.bottom <= container.bottom) return null;
  const tooTall = card.bottom - card.top > container.bottom - container.top;
  if (card.top < container.top || tooTall) return container.scrollTop + (card.top - container.top);
  return container.scrollTop + (card.bottom - container.bottom);
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    const hasOverflow = p.scrollHeight > p.clientHeight;
    if (isScrollable && hasOverflow) return p;
  }
  return null;
}

/**
 * Renders the `ask_questions` tool output. Pending: shows the question
 * list with radio/checkbox options + an "Other" free-text field per
 * question, and a Submit button that posts to /api/questions/answers.
 * Answered: read-only summary of the user's selections. Mirrors the
 * plan-approval-card pattern (server-truth via DB query, SSE
 * invalidation, sendMessage continuation).
 */
export function QuestionsCard({
  output,
  sendMessage,
  sessionId,
  turnActive,
}: QuestionsCardProps) {
  const qc = useQueryClient();
  const batch = output.questions;

  const [status, setStatus] = useState<CardStatus>('loading');
  const cardRef = useRef<HTMLDivElement>(null);
  const arrivalHandled = useRef(false);
  const [spotlit, setSpotlit] = useState(false);
  const dismiss = useDismissChatWaiting();

  // The in-progress answers live in a store keyed by the server-side batch id,
  // NOT in this component's state (FRONT-1685, root cause B). The chat list
  // replaces itself with the server's history when a turn settles, which can
  // remount this card under any key it might carry — so a draft held here
  // would not survive the turn it was typed during. See the store's own doc
  // comment for why no key at the message level is stable enough to rely on.
  const key = batch ? draftKey(sessionId, batch.id) : '';
  const draft = useQuestionDrafts((s) => s.drafts[key] ?? EMPTY_DRAFT);
  const toggleOption = useQuestionDrafts((s) => s.toggleOption);
  const setOther = useQuestionDrafts((s) => s.setOther);
  const clearDraft = useQuestionDrafts((s) => s.clearDraft);
  const { selections, others } = draft;

  // FRONT-1685: ask about THIS batch specifically, not "whatever's current"
  // — otherwise a newer, unrelated batch elsewhere in the session made this
  // card's own (possibly already-answered) status unreadable, forcing it
  // into a misleading "superseded" label even when it was genuinely
  // answered. Falls back to the session-wide key/lookup only in the
  // (impossible in practice, since `batch` is always defined here — see the
  // `!batch` early return below) case there's no batch id yet.
  const { data: dbState, isLoading: dbLoading } = useQuery({
    queryKey: batch ? questionKeys.byBatch(sessionId, batch.id) : questionKeys.bySession(sessionId),
    queryFn: () =>
      api.get<{ questions: QuestionsBatch | null }>(
        `/api/questions/current?sessionId=${encodeURIComponent(sessionId)}${
          batch ? `&batchId=${batch.id}` : ''
        }`,
      ),
    enabled: !!sessionId && !!batch,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const dbBatch = dbState?.questions ?? null;
  const dbMatches = !!dbBatch && !!batch && dbBatch.id === batch.id;

  useEffect(() => {
    if (dbLoading) {
      setStatus('loading');
      return;
    }
    if (!dbBatch) {
      setStatus('cancelled');
      return;
    }
    if (dbMatches) {
      setStatus(dbBatch.status);
      return;
    }
    setStatus('superseded');
  }, [dbLoading, dbBatch, dbMatches]);

  useEffect(() => {
    if (arrivalHandled.current || status === 'loading') return;
    arrivalHandled.current = true;
    if (!spotlightOnArrival(status, dbMatches, !!turnActive)) return;
    const el = cardRef.current;
    const container = el && scrollParent(el);
    if (el && container) {
      const c = container.getBoundingClientRect();
      const top = revealScrollTop({ top: c.top, bottom: c.bottom, scrollTop: container.scrollTop }, el.getBoundingClientRect());
      if (top !== null) container.scrollTo({ top, behavior: 'smooth' });
    }
    setSpotlit(true);
    const t = setTimeout(() => setSpotlit(false), SPOTLIGHT_MS);
    return () => {
      clearTimeout(t);
      setSpotlit(false);
    };
  }, [status, dbMatches, turnActive]);

  // Persisted answers (when status === 'answered') come from the DB
  // copy — the frozen tool output never gets them.
  const persistedAnswers = dbMatches ? dbBatch?.answers : undefined;

  const submit = useMutation({
    mutationFn: async (answers: QuestionAnswer[]) => {
      if (!batch) throw new Error('No batch to answer');
      return api.post<{ ok: boolean; reason?: string }>(
        '/api/questions/answers',
        { sessionId, batchId: batch.id, answers },
      );
    },
    onSuccess: (res, answers) => {
      if (!res.ok) {
        toast.info(res.reason ?? 'Answers could not be submitted');
        return;
      }
      toast.success('Answers submitted');
      // The draft has served its purpose — the answers are persisted server
      // side now, and the card renders them from the DB copy. Cleared only
      // here, never off a status read: a transient wrong status must not be
      // able to wipe input the user has not submitted yet.
      clearDraft(key);
      qc.invalidateQueries({ queryKey: chatKeys.history(sessionId) });
      qc.invalidateQueries({ queryKey: questionKeys.bySession(sessionId) });
      if (sendMessage && batch) {
        const summary = formatAnswersForChat(batch.questions, answers);
        void sendMessage(summary);
      }
    },
    onError: (err) => {
      toast.error((err as Error).message || 'Failed to submit answers');
    },
  });

  const canSubmit = useMemo(() => {
    if (!batch) return false;
    return batch.questions.every((q, i) => {
      const sel = selections[i] ?? [];
      if (sel.includes(OTHER)) {
        return (others[i] ?? '').trim().length > 0;
      }
      return sel.length > 0;
    });
  }, [batch, selections, others]);

  if (!batch) {
    return (
      <div className="border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground">
        Question batch was empty.
      </div>
    );
  }

  const isPending = status === 'pending' && dbMatches;
  const answerHere = isPending;
  const tone =
    status === 'answered'
      ? 'border-success/40 bg-success/5'
      : status === 'cancelled'
        ? 'border-muted bg-muted/30'
        : status === 'superseded'
          ? 'border-muted bg-muted/30 opacity-70'
          : status === 'loading'
            ? 'border-border bg-card'
            : 'border-primary/40 bg-primary/5';

  function toggle(qi: number, label: string, multi: boolean) {
    toggleOption(key, qi, label, multi);
  }

  function handleSubmit() {
    if (!batch) return;
    const answers: QuestionAnswer[] = batch.questions.map((q, i) => {
      const sel = selections[i] ?? [];
      const hasOther = sel.includes(OTHER);
      const selectedLabels = sel.filter((s) => s !== OTHER);
      const ans: QuestionAnswer = { selected: selectedLabels };
      if (hasOther) ans.other = (others[i] ?? '').trim();
      return ans;
    });
    submit.mutate(answers);
  }

  const header = (
    <>
        <StatusIcon status={status} />
        <span className="text-xs font-medium">
          {batch.questions.length === 1
            ? '1 question'
            : `${batch.questions.length} questions`}
        </span>
        <span className="ml-auto text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
          {status}
        </span>
    </>
  );
  const body = (
    <>
      <div className="px-3 py-3 space-y-4">
        {batch.questions.map((q, qi) => {
          const sel = selections[qi] ?? [];
          const isAnsweredView = status === 'answered' && persistedAnswers?.[qi];
          return (
            <div key={qi} className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[length:var(--text-2xs)] font-medium">
                  {q.header}
                </Badge>
                <span className="text-xs font-medium">{q.question}</span>
              </div>
              {isAnsweredView ? (
                <AnsweredView answer={persistedAnswers![qi]} />
              ) : (
                <PendingView
                  q={q}
                  qi={qi}
                  selected={sel}
                  otherText={others[qi] ?? ''}
                  disabled={!answerHere || submit.isPending}
                  onToggle={(label) => toggle(qi, label, q.multiSelect)}
                  onOtherChange={(v) => setOther(key, qi, v)}
                />
              )}
            </div>
          );
        })}
      </div>
      {answerHere && (
        <div className="px-3 py-2 border-t border-inherit flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={dismiss.isPending || submit.isPending || !!turnActive}
            onClick={() => dismiss.mutate({ sessionId })}
            tooltip="Skip this question. EDDY stops waiting on it."
          >
            Dismiss
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || submit.isPending || !!turnActive}
            onClick={handleSubmit}
            // Says what the user must do, not what the button will do for
            // them: nothing queues the submit — handleSubmit only ever runs
            // from this click — so promising it "submits once the reply
            // finishes" would leave someone waiting on an answer that never
            // gets sent. Their selections are safe in the meantime either way.
            tooltip={turnActive ? "You can submit once EDDY's reply finishes" : undefined}
          >
            {submit.isPending ? (
              <CircleNotch size={12} className="animate-spin mr-1" />
            ) : (
              <PaperPlaneTilt size={12} className="mr-1" />
            )}
            Submit answers
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div
      ref={cardRef}
      data-testid="questions-card"
      className={`rounded-lg border transition-shadow duration-500 ${tone} ${spotlit ? 'ring-2 ring-primary/60 ring-offset-2 ring-offset-background' : ''}`}
    >
      <div className="px-3 py-2 border-b border-inherit flex items-center gap-2">{header}</div>
      {body}
    </div>
  );
}

/**
 * FRONT-2104: the marker Eddy appends to the label of the ONE option it
 * recommends. Mirrors `RECOMMENDED_LABEL_SUFFIX` in `src/coding/prompt.ts`,
 * which is what actually instructs the model to emit it — the pair is pinned
 * by a drift test, because a reword on either side would silently stop the
 * chip rendering and nothing else would notice.
 */
export const RECOMMENDED_SUFFIX = '(Recommended)';

/**
 * Pure — split a model-authored option label into the text to display and
 * whether it carries the recommendation marker.
 *
 * The marker is stripped for DISPLAY only: the option's value stays the full
 * `opt.label`, so the stored answer, `formatAnswersForChat`, and the backend's
 * `shouldRecordBuildConsent` (which compares a selection against the
 * question's own first option label) all keep working on exactly the string
 * they did before.
 *
 * A label that doesn't end in the marker — including one where the model
 * spelled it differently — falls through unchanged and simply renders as
 * plain text, which is what Claude Code itself does. Degrading to "no chip"
 * is the right empty path; never to a missing or mangled label.
 */
export function splitRecommended(label: string): { text: string; recommended: boolean } {
  const trimmed = label.trimEnd();
  if (!trimmed.endsWith(RECOMMENDED_SUFFIX)) return { text: label, recommended: false };
  const text = trimmed.slice(0, -RECOMMENDED_SUFFIX.length).trimEnd();
  // A label that is ONLY the marker has no choice left to show — keep the
  // original rather than render an empty radio label.
  if (!text) return { text: label, recommended: false };
  return { text, recommended: true };
}

/** The recommendation chip. Deliberately quiet: it marks Eddy's pick, it does
 *  not pre-select the option or style the row as chosen (FRONT-1708).
 *
 *  Sits INSIDE the option's `<Label htmlFor>`, so it shares that option's hit
 *  area rather than introducing a second, overlapping one. `-top-px` is an
 *  optical correction: uppercase 2xs text has no descenders and reads as
 *  sitting low next to the xs label, so geometric alignment looks wrong here. */
export function RecommendedChip() {
  return (
    <span
      className="relative -top-px ml-1.5 inline-flex items-center rounded-full bg-primary/10 px-1.5 py-px align-middle text-[length:var(--text-2xs)] font-medium uppercase tracking-wider text-primary whitespace-nowrap"
    >
      Recommended
    </span>
  );
}

export function PendingView({
  q,
  qi,
  selected,
  otherText,
  disabled,
  allowOther = true,
  onToggle,
  onOtherChange,
}: {
  q: QuestionDef;
  qi: number;
  selected: string[];
  otherText: string;
  disabled: boolean;
  /** FRONT-1307: a consent gate takes no free text — an answer that can't be
   *  read as one of the offered options can't be acted on, and accepting one
   *  anyway means taking input and then doing the opposite of what it says.
   *  Every other question keeps its Other field. */
  allowOther?: boolean;
  onToggle: (label: string) => void;
  onOtherChange: (v: string) => void;
}) {
  const hasOther = selected.includes(OTHER);
  if (q.multiSelect) {
    return (
      <div className="space-y-1.5 pl-1">
        {q.options.map((opt, oi) => {
          const id = `q-${qi}-${oi}`;
          const checked = selected.includes(opt.label);
          const label = splitRecommended(opt.label);
          return (
            <div key={opt.label} className="flex items-start gap-2">
              <Checkbox
                id={id}
                checked={checked}
                disabled={disabled}
                onCheckedChange={() => onToggle(opt.label)}
              />
              <Label
                htmlFor={id}
                className="text-xs font-normal leading-tight cursor-pointer text-pretty"
              >
                <span className="font-medium">{label.text}</span>
                {label.recommended && <RecommendedChip />}
                {opt.description && (
                  <span className="block text-muted-foreground">
                    {opt.description}
                  </span>
                )}
              </Label>
            </div>
          );
        })}
        {allowOther && (
          <OtherField
            qi={qi}
            checked={hasOther}
            multi
            disabled={disabled}
            value={otherText}
            onToggle={() => onToggle(OTHER)}
            onChange={onOtherChange}
          />
        )}
      </div>
    );
  }
  return (
    <RadioGroup
      value={selected[0] ?? ''}
      onValueChange={(v) => onToggle(v)}
      disabled={disabled}
      className="space-y-1.5 pl-1"
    >
      {q.options.map((opt, oi) => {
        const id = `q-${qi}-${oi}`;
        const label = splitRecommended(opt.label);
        return (
          <div key={opt.label} className="flex items-start gap-2">
            <RadioGroupItem id={id} value={opt.label} />
            <Label
              htmlFor={id}
              className="text-xs font-normal leading-tight cursor-pointer text-pretty"
            >
              <span className="font-medium">{label.text}</span>
              {label.recommended && <RecommendedChip />}
              {opt.description && (
                <span className="block text-muted-foreground">
                  {opt.description}
                </span>
              )}
            </Label>
          </div>
        );
      })}
      {allowOther && (
        <div className="flex items-start gap-2">
          <RadioGroupItem id={`q-${qi}-other`} value={OTHER} />
          <div className="flex-1 space-y-1">
            <Label
              htmlFor={`q-${qi}-other`}
              className="text-xs font-normal leading-tight cursor-pointer"
            >
              <span className="font-medium">Other</span>
            </Label>
            {hasOther && (
              <input
                type="text"
                value={otherText}
                onChange={(e) => onOtherChange(e.target.value)}
                placeholder="Type your answer…"
                className="w-full text-xs rounded-md border border-input bg-transparent px-2 py-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            )}
          </div>
        </div>
      )}
    </RadioGroup>
  );
}

function OtherField({
  qi,
  checked,
  multi,
  disabled,
  value,
  onToggle,
  onChange,
}: {
  qi: number;
  checked: boolean;
  multi: boolean;
  disabled: boolean;
  value: string;
  onToggle: () => void;
  onChange: (v: string) => void;
}) {
  const id = `q-${qi}-other`;
  return (
    <div className="flex items-start gap-2">
      {multi ? (
        <Checkbox
          id={id}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onToggle}
        />
      ) : null}
      <div className="flex-1 space-y-1">
        <Label
          htmlFor={id}
          className="text-xs font-normal leading-tight cursor-pointer"
        >
          <span className="font-medium">Other</span>
        </Label>
        {checked && (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer…"
            disabled={disabled}
            className="w-full text-xs rounded-md border border-input bg-transparent px-2 py-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
          />
        )}
      </div>
    </div>
  );
}

export function AnsweredView({ answer }: { answer: QuestionAnswer }) {
  return (
    <div className="pl-1 text-xs text-foreground/90 space-y-0.5">
      {answer.selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {answer.selected.map((s) => (
            <Badge key={s} variant="secondary" className="text-[length:var(--text-2xs)]">
              {splitRecommended(s).text}
            </Badge>
          ))}
        </div>
      )}
      {answer.other && (
        <div className="italic text-muted-foreground">“{answer.other}”</div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: CardStatus }) {
  if (status === 'answered')
    return <CheckCircle size={14} weight="fill" className="text-success" />;
  if (status === 'superseded' || status === 'cancelled')
    return <Prohibit size={14} weight="fill" className="text-muted-foreground" />;
  if (status === 'loading')
    return <CircleNotch size={14} weight="bold" className="text-muted-foreground animate-spin" />;
  return <ChatCircleText size={14} weight="fill" className="text-primary" />;
}

export function formatAnswersForChat(
  questions: QuestionDef[],
  answers: QuestionAnswer[],
): string {
  const lines = questions.map((q, i) => {
    const ans = answers[i];
    if (!ans) return `• ${q.question} — (no answer)`;
    const parts = [...ans.selected];
    if (ans.other) parts.push(`Other: ${ans.other}`);
    return `• ${q.question} → ${parts.join(', ') || '(no selection)'}`;
  });
  // Frame the answers as a directive so the assistant merges them into my
  // ORIGINAL request (already in the conversation history) and proceeds with
  // the normal flow instead of re-asking or stalling. We deliberately do NOT
  // tell it to "stop asking": a genuinely blocking follow-up may still be
  // warranted, and the pre-seeded "don't ask again" read as presumptuous since
  // the user never chose it (FRONT-492). This just hands the answers back.
  //
  // FRONT-2320: the server recognises this message as the answers to a
  // suspended inline skill's question batch by finding EVERY question's
  // text in it (`decideInlineSkillResume`, src/agents/inline-skill-resume.ts)
  // — keep each `q.question` verbatim here or that skill will not resume.
  return (
    `Here are my answers to your clarifying questions. ` +
    `Please use them to continue with my original request:\n${lines.join('\n')}`
  );
}
